// A person's own layout of a list table: which columns they see, in which order
// and how wide. Asked for on Taken (Camiel, 2026-09-17: "kolominstellingen
// wijzigen en kunnen verslepen"), and what the design system promises every
// operational table: show, hide, reorder, resize (design-system/data.html, 07).
//
// Kept on the account, so it follows the person to another PC (Camiel,
// 2026-09-17: "graag met account mee"; API /table-layouts/{table}), with a copy
// in this browser so the list opens in their layout straight away. The wiring
// is in components/table/useColumnLayout.ts; this file is only the rules.
//
// Widths have two states. Until someone drags a column edge the browser sizes
// the columns to their content, exactly as the table always did. The first drag
// freezes every visible column at the width it is drawn at — so nothing jumps —
// and from then on a column is as wide as it was made: the space left over
// stays empty at the right, and a table wider than the screen scrolls. Resizing
// one column only pushes the columns after it along, so the edge you hold stays
// under the pointer.

export interface ColumnDef {
  id: string;
  // Always shown: the column that says what a row IS. Without it the rows
  // cannot be told apart — and a table with every column off is no table.
  locked?: boolean;
  // Its width once widths are frozen and it has none of its own: a column
  // switched on after the freeze, or one added to the screen later. Also what a
  // double-click on its edge goes back to.
  defaultWidth: number;
  // Offered in the column menu but off until someone switches it on: an extra
  // column most people do not need, like a licence plate beside the fleet number.
  defaultHidden?: boolean;
}

export interface ColumnLayout {
  // Every column, the hidden ones included, so a column switched back on
  // returns to the place it had instead of turning up at the end.
  order: string[];
  hidden: string[];
  // Pixels per column. Empty = sized by the browser to the content.
  widths: Record<string, number>;
}

// Narrow enough to tuck a column away, wide enough to still find its edge.
export const MIN_COLUMN_PX = 48;
export const MAX_COLUMN_PX = 1200;

function defaultHiddenIds(defs: readonly ColumnDef[]): string[] {
  return defs.filter((d) => d.defaultHidden && !d.locked).map((d) => d.id);
}

export function defaultColumnLayout(defs: readonly ColumnDef[]): ColumnLayout {
  return { order: defs.map((d) => d.id), hidden: defaultHiddenIds(defs), widths: {} };
}

// To the hundredth, not the whole pixel. Content-sized columns come out at
// widths like 53.47px, and freezing that as 53 cut "PRIO" off by a fraction of
// a pixel; rounding up instead would make the frozen table a few pixels wider
// than the one it replaces — enough for a scrollbar the moment you touch an edge.
export function clampColumnWidth(px: number): number {
  return Math.round(Math.min(MAX_COLUMN_PX, Math.max(MIN_COLUMN_PX, px)) * 100) / 100;
}

// The stored order without the columns that no longer exist, plus the ones
// added since it was saved — each right after the column it follows by
// default, which is where it was designed to sit, rather than at the far end.
function completeOrder(stored: string[], defs: readonly ColumnDef[]): string[] {
  const out = [...new Set(stored)];
  defs.forEach((def, i) => {
    if (out.includes(def.id)) return;
    let at = 0;
    for (let j = i - 1; j >= 0; j--) {
      const k = out.indexOf(defs[j].id);
      if (k !== -1) {
        at = k + 1;
        break;
      }
    }
    out.splice(at, 0, def.id);
  });
  return out;
}

// A stored layout made to fit the columns the screen has today. Anything
// unreadable falls back rather than drawing a table without columns: a value
// written by an older version, one naming columns that have since gone, or
// nothing at all (null — the default).
export function normalizeColumnLayout(data: unknown, defs: readonly ColumnDef[]): ColumnLayout {
  if (typeof data !== "object" || data === null || Array.isArray(data)) return defaultColumnLayout(defs);
  const d = data as Record<string, unknown>;
  const byId = new Map(defs.map((def) => [def.id, def]));
  const knownIds = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === "string" && byId.has(x)) : [];

  // A column the screen added since the layout was saved starts as it would by
  // default: an extra column stays off until switched on.
  const storedOrder = knownIds(d["order"]);
  const addedHidden = defaultHiddenIds(defs).filter((id) => !storedOrder.includes(id));
  const hidden = [...new Set([...knownIds(d["hidden"]), ...addedHidden])].filter((id) => !byId.get(id)?.locked);
  const widths: Record<string, number> = {};
  const rawWidths = d["widths"];
  if (typeof rawWidths === "object" && rawWidths !== null && !Array.isArray(rawWidths)) {
    for (const [id, px] of Object.entries(rawWidths as Record<string, unknown>)) {
      if (byId.has(id) && typeof px === "number" && Number.isFinite(px)) widths[id] = clampColumnWidth(px);
    }
  }
  return { order: completeOrder(storedOrder, defs), hidden, widths };
}

// The same from its JSON, as the browser copy keeps it — including a copy cut
// off mid-write.
export function parseColumnLayout(raw: string | null, defs: readonly ColumnDef[]): ColumnLayout {
  if (!raw) return defaultColumnLayout(defs);
  try {
    return normalizeColumnLayout(JSON.parse(raw), defs);
  } catch {
    return defaultColumnLayout(defs);
  }
}

export function visibleColumns<T extends ColumnDef>(layout: ColumnLayout, defs: readonly T[]): T[] {
  const byId = new Map(defs.map((d) => [d.id, d]));
  return layout.order
    .map((id) => byId.get(id))
    .filter((d): d is T => d !== undefined && (d.locked === true || !layout.hidden.includes(d.id)));
}

// The columns in their current order, hidden ones included — the list the
// column menu shows.
export function orderedColumns<T extends ColumnDef>(layout: ColumnLayout, defs: readonly T[]): T[] {
  const byId = new Map(defs.map((d) => [d.id, d]));
  return layout.order.map((id) => byId.get(id)).filter((d): d is T => d !== undefined);
}

// Put `dragged` where `target` is: moving right it lands after the target,
// moving left before it. Returns the same layout when nothing moves, so a drop
// on itself does not rewrite storage.
export function moveColumn(layout: ColumnLayout, dragged: string, target: string): ColumnLayout {
  if (dragged === target) return layout;
  const from = layout.order.indexOf(dragged);
  const to = layout.order.indexOf(target);
  if (from === -1 || to === -1) return layout;
  const order = [...layout.order];
  order.splice(from, 1);
  order.splice(to, 0, dragged);
  return { ...layout, order };
}

// Which side of `target` a dragged column would land on — where to draw the
// line while it is held over it.
export function dropSide(layout: ColumnLayout, dragged: string, target: string): "before" | "after" | null {
  const from = layout.order.indexOf(dragged);
  const to = layout.order.indexOf(target);
  if (from === -1 || to === -1 || from === to) return null;
  return from < to ? "after" : "before";
}

// One place up or down the full list: the keyboard way to reorder.
export function moveColumnBy(layout: ColumnLayout, id: string, step: -1 | 1): ColumnLayout {
  const from = layout.order.indexOf(id);
  const to = from + step;
  if (from === -1 || to < 0 || to >= layout.order.length) return layout;
  return moveColumn(layout, id, layout.order[to]);
}

export function toggleColumn(layout: ColumnLayout, id: string, defs: readonly ColumnDef[]): ColumnLayout {
  const def = defs.find((d) => d.id === id);
  if (!def || def.locked) return layout;
  const hidden = layout.hidden.includes(id) ? layout.hidden.filter((h) => h !== id) : [...layout.hidden, id];
  return { ...layout, hidden };
}

export function hasFixedWidths(layout: ColumnLayout): boolean {
  return Object.keys(layout.widths).length > 0;
}

export function columnWidth(layout: ColumnLayout, def: ColumnDef): number {
  return layout.widths[def.id] ?? def.defaultWidth;
}

export function setColumnWidth(layout: ColumnLayout, id: string, px: number): ColumnLayout {
  const width = clampColumnWidth(px);
  if (layout.widths[id] === width) return layout;
  return { ...layout, widths: { ...layout.widths, [id]: width } };
}

// Every column that has no width yet gets the one it is drawn at (`measured`,
// in pixels). Called when a column edge first moves, so the switch from
// content-sized to fixed columns happens without anything shifting.
export function freezeColumnWidths(layout: ColumnLayout, measured: Record<string, number>): ColumnLayout {
  const widths = { ...layout.widths };
  let changed = false;
  for (const [id, px] of Object.entries(measured)) {
    if (widths[id] !== undefined || !layout.order.includes(id) || !Number.isFinite(px) || px <= 0) continue;
    widths[id] = clampColumnWidth(px);
    changed = true;
  }
  return changed ? { ...layout, widths } : layout;
}

export function isDefaultColumnLayout(layout: ColumnLayout, defs: readonly ColumnDef[]): boolean {
  const hiddenByDefault = defaultHiddenIds(defs);
  return (
    layout.hidden.length === hiddenByDefault.length &&
    hiddenByDefault.every((id) => layout.hidden.includes(id)) &&
    !hasFixedWidths(layout) &&
    layout.order.length === defs.length &&
    layout.order.every((id, i) => defs[i].id === id)
  );
}

// What goes to the account: the default as null — kept as no row at all — so
// someone who went back to it also gets a better default when the screen's own
// columns change later.
export function storedColumnLayout(layout: ColumnLayout, defs: readonly ColumnDef[]): ColumnLayout | null {
  return isDefaultColumnLayout(layout, defs) ? null : layout;
}

// ── The copy in this browser ────────────────────────────────────────────────
// Per user, not per browser: on a shared PC the next person must not open the
// list in the layout of the one before them while theirs is still on its way.
export function columnLayoutCacheKey(userId: string, table: string): string {
  return `wlx:tableLayout:${userId}:${table}`;
}

// Storage throws in a private window and when site data is blocked: a column
// layout is never worth an error screen — the account still has it.
export function readCachedColumnLayout(key: string, defs: readonly ColumnDef[]): ColumnLayout {
  try {
    return parseColumnLayout(localStorage.getItem(key), defs);
  } catch {
    return defaultColumnLayout(defs);
  }
}

export function writeCachedColumnLayout(key: string, layout: ColumnLayout, defs: readonly ColumnDef[]): void {
  try {
    if (isDefaultColumnLayout(layout, defs)) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(layout));
  } catch {
    /* nothing to do — the account keeps the layout */
  }
}
